/**
 * Smart Shikka AI Core — PR96
 * First Real Chat Send — Owner Approved / Staging Only
 * Registry — Deep Frozen — No Tools/Agents/Workflows
 */
function deepFreeze(obj) {
    if (obj && typeof obj === "object") {
        Object.freeze(obj);
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (val && typeof val === "object" && !Object.isFrozen(val)) {
                deepFreeze(val);
            }
        }
    }
    return obj;
}
const BLOCKED_ACTIONS = [
    "agent_execution", "browser_action", "child_process",
    "desktop_action", "file_read", "file_write",
    "old_assistant_alias", "secret_literal", "shell_command",
    "tool_execution", "unapproved_db_write", "unapproved_file_write",
    "unapproved_network_call", "unknown_provider_call",
    "unknown_tool_execution", "unredacted_secret", "unsafe_env_dump",
    "workflow_execution", "runtime_unlock", "gate_unlock",
    "render_document", "inject_icon_runtime",
    "message_send_without_approval", "chat_runtime_start_without_gate",
    "user_prompt_send", "secret_read",
    "model_inference_without_healthcheck",
    "unredacted_prompt", "unredacted_response",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    providerCallAllowedInPr96: false,
    secretLiteralAllowedInPr96: false,
    secretReadAllowedInPr96: false,
    runtimeUnlockAllowedInPr96: false,
    gateUnlockAllowedInPr96: false,
    agentExecutionAllowedInPr96: false,
    toolExecutionAllowedInPr96: false,
    workflowExecutionAllowedInPr96: false,
    documentRenderAllowedInPr96: false,
    iconInjectionAllowedInPr96: false,
    browserAutomationAllowedInPr96: false,
    desktopAutomationAllowedInPr96: false,
    fileMutationAllowedInPr96: false,
    fileReadAllowedInPr96: false,
    dbWriteAllowedInPr96: false,
    supabaseAllowedInPr96: false,
    shellCommandAllowedInPr96: false,
    childProcessAllowedInPr96: false,
    userPromptAllowedInPr96: false,
    unredactedPromptAllowedInPr96: false,
    unredactedResponseAllowedInPr96: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "controlled_chat_send_summary",
        displayName: "Controlled Chat Send Summary",
        description: "Summary of first real controlled chat send. Owner-approved, staging-only, provider healthcheck required, budget-limited, fully redacted.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send", "smart-shikka-assistant-provider-healthcheck", "smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["readonly_message_thread_preview"],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot"],
        relatedApprovalStatePreviewIds: ["readonly_approval_summary_state"],
        relatedActionRequestPreviewIds: ["readonly_action_request_summary"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["First controlled chat send — staging only", "Owner approval required", "No tools/agents/workflows"],
        futurePrScope: ["Multi-turn chat", "Production chat send", "Tool-assisted chat"],
        notes: ["Staging environment only", "Single controlled send", "Full redaction enforced"],
    },
    {
        id: "owner_approved_chat_send_gate",
        displayName: "Owner Approved Chat Send Gate",
        description: "Gate requiring explicit owner approval before any chat send. No send without gate open. Dual approval enforced.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send", "smart-shikka-assistant-readonly-approval-state-preview"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_dual_approval_required_state", "readonly_owner_approval_required_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Owner must approve chat send", "Dual approval required", "No send without explicit gate open"],
        futurePrScope: ["Streamlined approval for trusted contexts"],
        notes: ["Gate locked until owner explicitly approves", "Audit evidence generated on approval"],
    },
    {
        id: "staging_only_chat_send_gate",
        displayName: "Staging Only Chat Send Gate",
        description: "Chat send restricted to staging environment only. Production sends blocked. Environment check enforced before send.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Staging environment only", "Production sends blocked", "Environment verification required"],
        futurePrScope: ["Production chat send with additional approval layer"],
        notes: ["Only STAGING env accepted", "Production blocked at gate level"],
    },
    {
        id: "provider_healthcheck_required_gate",
        displayName: "Provider Healthcheck Required Gate",
        description: "Provider healthcheck must pass before chat send. No send to unhealthy provider. Timeout budget enforced.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send", "smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Healthcheck must pass", "No send to unhealthy provider", "Timeout budget enforced"],
        futurePrScope: ["Cached healthcheck results", "Provider failover"],
        notes: ["PR94 healthcheck must succeed before PR96 chat send", "Synthetic healthcheck payload verified"],
    },
    {
        id: "prompt_redaction_contract",
        displayName: "Prompt Redaction Contract",
        description: "All prompts must be redacted before logging/persistence. No raw user content stored. PII stripped before audit.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send", "smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Prompts redacted before storage", "No raw user content logged", "PII stripped"],
        futurePrScope: ["Selective redaction with user consent"],
        notes: ["Prompt → [REDACTED_PROMPT]", "PII → [REDACTED_PII]", "Audit logs contain redacted version only"],
    },
    {
        id: "response_redaction_contract",
        displayName: "Response Redaction Contract",
        description: "All provider responses must be redacted before logging/persistence. No raw AI output stored. Secrets in response stripped.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send", "smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Responses redacted before storage", "No raw AI output logged", "Secrets stripped from response"],
        futurePrScope: ["Selective response retention with consent"],
        notes: ["Response → [REDACTED_RESPONSE]", "Secrets → [REDACTED_SECRET_REF]"],
    },
    {
        id: "budget_limit_contract",
        displayName: "Budget Limit Contract",
        description: "Chat send has strict token/cost budget. Exceeding budget blocks send. No unlimited usage.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send", "smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Budget limit enforced", "Exceeding budget blocks send", "No unlimited usage allowed"],
        futurePrScope: ["Dynamic budget allocation", "Per-user budget tracking"],
        notes: ["Max tokens per send enforced", "Cost cap per session", "Budget check before provider call"],
    },
    {
        id: "no_tool_execution_boundary",
        displayName: "No Tool Execution Boundary",
        description: "Chat send must not trigger tool execution. Tools are blocked in PR96. Pure chat completion only.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["tool_execution_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Tool execution blocked", "Pure chat completion only", "No function calling"],
        futurePrScope: ["Tool-assisted chat with separate approval"],
        notes: ["tool_execution blocked", "unknown_tool_execution blocked", "No function_call in provider request"],
    },
    {
        id: "no_agent_execution_boundary",
        displayName: "No Agent Execution Boundary",
        description: "Chat send must not trigger agent execution or workflows. Single completion only. No multi-step agents.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: ["readonly_agent_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Agent execution blocked", "No multi-step agents", "Single completion only", "Workflow execution blocked"],
        futurePrScope: ["Agent-assisted chat with separate approval gate"],
        notes: ["agent_execution blocked", "workflow_execution blocked", "No autonomous agent loops"],
    },
    {
        id: "chat_send_audit_evidence",
        displayName: "Chat Send Audit Evidence",
        description: "Every chat send generates audit evidence. Redacted prompt, redacted response, approval chain, budget usage, environment tag.",
        sourceModules: ["smart-shikka-assistant-controlled-chat-send", "smart-shikka-assistant-no-runtime-safety-audit-pack"],
        targetPreviewModules: ["smart-shikka-assistant-controlled-chat-send"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Audit evidence mandatory", "No send without audit trail", "Redacted evidence only"],
        futurePrScope: ["Audit dashboard", "Compliance reporting"],
        notes: ["Evidence: approval_chain, redacted_prompt, redacted_response, budget_usage, env_tag", "All evidence redacted"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantControlledChatSendEntries() {
    return registry;
}
export function getSmartShikkaAssistantControlledChatSendEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantControlledChatSendRegistry.js.map